import React, { useState, useEffect, useRef } from 'react';
import AcademyHeader from '../../../components/LP/AcademyHeader';
import LPFooter from '../../../components/LP/LPFooter';
import SEO from '../../../components/SEO/SEO';
import { decodeBinaryFields, bufferToBase64, isWebAuthnSupported } from '../../../lib/webauthn';
import AcademyPasskeyManage from './AcademyPasskeyManage';

// YouTube IFrame Player APIのスクリプトをページに1回だけ読み込む
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return;
  if (document.getElementById('youtube-iframe-api')) return;
  const tag = document.createElement('script');
  tag.id = 'youtube-iframe-api';
  tag.src = 'https://www.youtube.com/iframe_api';
  document.body.appendChild(tag);
}

// 実際に再生開始・最後まで視聴したタイミングでonPlay/onEndedを呼ぶYouTubeプレイヤー
const YouTubePlayer = ({ videoId, title, onPlay, onEnded }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const firedRef = useRef({ play: false, ended: false });

  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled || !containerRef.current || playerRef.current || !window.YT) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        events: {
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING && !firedRef.current.play) {
              firedRef.current.play = true;
              onPlay();
            } else if (e.data === window.YT.PlayerState.ENDED && !firedRef.current.ended) {
              firedRef.current.ended = true;
              onEnded();
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      loadYouTubeApi();
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prevReady) prevReady(); init(); };
    }

    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return <div ref={containerRef} title={title} className="w-full h-full" />;
};

const AcademyMembersPage = () => {
  const [step, setStep] = useState('email'); // email | otp | videos
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [token, setToken] = useState(localStorage.getItem('academy_token') || '');
  const [videos, setVideos] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showPasskeyManage, setShowPasskeyManage] = useState(false);
  const isAdmin = localStorage.getItem('admin_auth') === 'true';

  // 視聴ログを送る（監査ログに記録。管理者の全動画プレビューではtokenが空なので送られない）
  const logView = (title, event) => {
    if (!token || !title) return;
    fetch('/api/admin-video.php?action=log-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, title, event }),
    }).catch(() => {});
  };

  // 既存トークンで自動ログイン試行
  useState(() => {
    // 管理者は認証スキップして全動画表示
    if (isAdmin && !token) {
      fetch('/api/admin-read.php?file=academy-videos&t=' + Date.now())
        .then((r) => r.ok ? r.json() : [])
        .then((data) => { setVideos(data.map((v) => ({ ...v, canWatch: true }))); setStep('videos'); })
        .catch(() => {});
      return;
    }
    if (token) {
      fetch('/api/admin-video.php?action=videos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) { setVideos(data.videos); setStep('videos'); }
          else { localStorage.removeItem('academy_token'); setToken(''); }
        })
        .catch(() => { localStorage.removeItem('academy_token'); setToken(''); });
    }
  });

  const sendOtp = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch('/api/admin-video.php?action=send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setStep('otp');
        setMessage('認証コードをメールに送信しました');
      } else {
        setMessage(data.message || 'エラーが発生しました');
      }
    } catch { setMessage('エラーが発生しました'); }
    setLoading(false);
  };

  const loginWithPasskey = async () => {
    if (!isWebAuthnSupported()) { setMessage('このブラウザはパスキーに対応していません'); return; }
    setPasskeyLoading(true);
    setMessage('');
    try {
      const optRes = await fetch('/api/admin-passkey.php?action=academy-login-options', { method: 'POST' });
      const optData = await optRes.json();
      if (!optData.success) { setMessage(optData.message || 'パスキーでのログインに失敗しました'); setPasskeyLoading(false); return; }

      const publicKey = decodeBinaryFields(optData.options).publicKey;
      const cred = await navigator.credentials.get({ publicKey });

      const verifyRes = await fetch('/api/admin-passkey.php?action=academy-login-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: bufferToBase64(cred.rawId),
          clientDataJSON: bufferToBase64(cred.response.clientDataJSON),
          authenticatorData: bufferToBase64(cred.response.authenticatorData),
          signature: bufferToBase64(cred.response.signature),
        }),
      });
      const data = await verifyRes.json();
      if (data.success) {
        localStorage.setItem('academy_token', data.token);
        setToken(data.token);
        const vRes = await fetch('/api/admin-video.php?action=videos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: data.token }),
        });
        const vData = await vRes.json();
        if (vData.success) setVideos(vData.videos);
        setStep('videos');
      } else {
        setMessage(data.message || 'パスキーでのログインに失敗しました');
      }
    } catch (err) {
      setMessage(err && err.name === 'NotAllowedError' ? 'パスキーの操作がキャンセルされました' : 'パスキーでのログインに失敗しました');
    }
    setPasskeyLoading(false);
  };

  const verifyOtp = async () => {
    if (!otp.trim()) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch('/api/admin-video.php?action=verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('academy_token', data.token);
        setToken(data.token);
        // 動画取得
        const vRes = await fetch('/api/admin-video.php?action=videos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: data.token }),
        });
        const vData = await vRes.json();
        if (vData.success) setVideos(vData.videos);
        setStep('videos');
      } else {
        setMessage(data.message || '認証に失敗しました');
      }
    } catch { setMessage('エラーが発生しました'); }
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('academy_token');
    setToken('');
    setStep('email');
    setEmail('');
    setOtp('');
    setVideos([]);
  };

  const categories = [...new Set(videos.map((v) => v.category).filter(Boolean))];
  const filtered = filter ? videos.filter((v) => v.category === filter) : videos;

  const getYouTubeId = (url) => {
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    return m ? m[1] : null;
  };

  return (
    <div className="min-h-screen flex flex-col font-noto bg-lp-cream text-lp-charcoal">
      <SEO title="アカデミー会員ページ｜SrWith" description="SrWithアカデミー会員限定の動画コンテンツ" />
      <AcademyHeader />

      <main className="flex-grow pt-[48px]">
        {step === 'videos' ? (
          <section className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-5">
              <div className="flex items-center justify-between mb-8">
                <h1 className="font-noto-serif text-2xl md:text-3xl font-bold">動画コンテンツ</h1>
                <div className="flex items-center gap-4">
                  {token && (
                    <button onClick={() => setShowPasskeyManage(true)} className="text-sm text-gray-500 hover:text-gray-700">🔑 パスキー</button>
                  )}
                  <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-700">ログアウト</button>
                </div>
              </div>
              {showPasskeyManage && <AcademyPasskeyManage token={token} onClose={() => setShowPasskeyManage(false)} />}

              {categories.length > 0 && (
                <div className="flex gap-2 mb-6 flex-wrap">
                  <button onClick={() => setFilter('')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${!filter ? 'bg-lp-rose text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>すべて</button>
                  {categories.map((c) => (
                    <button key={c} onClick={() => setFilter(c)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${filter === c ? 'bg-lp-rose text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>{c}</button>
                  ))}
                </div>
              )}

              {filtered.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center text-gray-500 shadow">
                  動画コンテンツはまだありません
                </div>
              ) : (
                <div className="space-y-6">
                  {filtered.map((v) => {
                    const ytId = getYouTubeId(v.url);
                    return (
                      <div key={v.id} className={`bg-white rounded-2xl shadow overflow-hidden ${!v.canWatch ? 'opacity-60' : ''}`}>
                        {v.canWatch ? (
                          <>
                            {ytId && (
                              <div className="aspect-video">
                                <YouTubePlayer videoId={ytId} title={v.title}
                                  onPlay={() => logView(v.title, 'start')}
                                  onEnded={() => logView(v.title, 'end')} />
                              </div>
                            )}
                            <div className="p-5">
                              <div className="flex items-center gap-2 mb-2">
                                <h2 className="font-bold text-lg">{v.title}</h2>
                                {v.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">{v.category}</span>}
                              </div>
                              {v.description && <p className="text-sm text-gray-600 mb-2">{v.description}</p>}
                              {!ytId && (
                                <a href={v.url} target="_blank" rel="noopener noreferrer" onClick={() => logView(v.title)}
                                  className="inline-block px-6 py-2 bg-lp-rose text-white font-bold rounded-lg hover:opacity-90 text-sm">動画を見る</a>
                              )}
                              {v.attachments?.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                  <p className="text-xs font-bold text-gray-500 mb-2">添付資料</p>
                                  <div className="flex flex-wrap gap-2">
                                    {v.attachments.map((a, i) => (
                                      <a key={i} href={a.url} download
                                        className="inline-flex items-center px-4 py-2 rounded-lg border-2 border-lp-rose text-lp-rose text-sm font-bold hover:bg-lp-rose hover:text-white transition-colors">
                                        <span className="truncate max-w-[220px]">{a.name}</span>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <h2 className="font-bold text-lg">{v.title}</h2>
                              {v.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">{v.category}</span>}
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-600">
                                {v.access === 'academy' ? 'アカデミー受講者限定' : '契約者限定'}
                              </span>
                            </div>
                            {v.description && <p className="text-sm text-gray-500 mb-2">{v.description}</p>}
                            <p className="text-sm text-gray-400">この動画を視聴するには契約が必要です</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        ) : (
          <section className="py-20 md:py-28">
            <div className="max-w-md mx-auto px-5">
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg text-center">
                <h1 className="font-noto-serif text-xl md:text-2xl font-bold mb-2">アカデミー会員ページ</h1>
                <p className="text-sm text-gray-500 mb-8">アカデミー契約者限定の動画コンテンツ</p>

                {message && (
                  <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.includes('送信しました') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message}
                  </div>
                )}

                {step === 'email' && (
                  <div className="space-y-4">
                    <button onClick={loginWithPasskey} disabled={passkeyLoading}
                      className="w-full py-3 bg-lp-rose text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-50 text-sm">
                      {passkeyLoading ? 'パスキーを確認中...' : '🔑 パスキーでログイン'}
                    </button>
                    <div className="flex items-center gap-2 text-gray-300 text-xs">
                      <div className="flex-1 h-px bg-gray-200" />または<div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="登録メールアドレスを入力"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && sendOtp()} />
                    <button onClick={sendOtp} disabled={loading || !email.trim()}
                      className="w-full py-3 bg-white border-2 border-lp-rose text-lp-rose font-bold rounded-lg hover:bg-lp-rose/5 disabled:opacity-50 text-sm">
                      {loading ? '送信中...' : '認証コードを送信'}
                    </button>
                  </div>
                )}

                {step === 'otp' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">{email} に認証コードを送信しました</p>
                    <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)}
                      placeholder="6桁の認証コードを入力" maxLength={6}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm text-center tracking-widest text-lg"
                      onKeyDown={(e) => e.key === 'Enter' && verifyOtp()} />
                    <button onClick={verifyOtp} disabled={loading || otp.length < 6}
                      className="w-full py-3 bg-lp-rose text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-50 text-sm">
                      {loading ? '認証中...' : 'ログイン'}
                    </button>
                    <button onClick={() => { setStep('email'); setMessage(''); }}
                      className="text-sm text-gray-500 hover:text-gray-700">メールアドレスを変更</button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      <LPFooter />
    </div>
  );
};

export default AcademyMembersPage;
